import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import BottomNav from "../components/BottomNav";

const SpinPage = () => {
  const { user, refreshUser, authAxios } = useAuth();
  const { t } = useLanguage();
  const [spinStatus, setSpinStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [reward, setReward] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const [countdown, setCountdown] = useState("");

  useEffect(() => { fetchSpinStatus(); }, []);

  const fetchSpinStatus = async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/spin/status/${user.id}`);
      setSpinStatus(res.data);
    } catch (e) { console.error(e); }
  };

  const segments = [
    { value: 0, color: "#1a1a1a", label: "0" },
    { value: 5, color: "#FFD700", label: "5" },
    { value: 10, color: "#1a1a1a", label: "10" },
    { value: 15, color: "#FFD700", label: "15" },
    { value: 25, color: "#1a1a1a", label: "25" },
    { value: 50, color: "#FFD700", label: "50" },
    { value: 100, color: "#1a1a1a", label: "100" },
    { value: 200, color: "#FFD700", label: "200" },
    { value: 500, color: "#1a1a1a", label: "500" },
    { value: 1000, color: "#FFD700", label: "1K" },
  ];

  const doSpin = async () => {
    if (!spinStatus?.can_spin || spinning) return;
    setSpinning(true);
    setReward(null);
    setShowReward(false);
    
    try {
      const api = authAxios();
      const res = await api.post(`/spin/${user.id}`);
      const wonReward = res.data.reward;
      
      const segmentIndex = segments.findIndex(s => s.value === wonReward);
      const numSegments = segments.length;
      const segmentAngle = 360 / numSegments;
      
      const extraSpins = 5;
      const segmentCenter = segmentIndex * segmentAngle + (segmentAngle / 2);
      const targetRotation = 360 - segmentCenter;
      const totalRotation = (extraSpins * 360) + targetRotation;
      
      setRotation(totalRotation);
      
      setTimeout(() => {
        setSpinning(false);
        setReward(wonReward);
        setShowReward(true);
        if (wonReward > 0) {
          toast.success(`🎉 Congratulations! You won ${wonReward} LTG!`);
        } else {
          toast.info("Better luck next time!");
        }
        refreshUser();
        fetchSpinStatus();
      }, 5000);
    } catch (e) {
      setSpinning(false);
      toast.error("Spin failed");
    }
  };

  const getCountdown = () => {
    if (!spinStatus?.next_spin_available) return "";
    const next = new Date(spinStatus.next_spin_available);
    const diff = next - new Date();
    if (diff <= 0) return "";
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return `${hours}h ${mins}m ${secs}s`;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getCountdown());
    }, 1000);
    return () => clearInterval(timer);
  }, [spinStatus]);

  return (
    <div className="min-h-screen pb-24 px-4 pt-6 bg-black" data-testid="spin-page">
      <div className="text-center mb-6">
        <h1 className="font-manrope font-bold text-3xl text-gold-gradient">🎰 Lucky Spin</h1>
        <p className="text-muted-foreground text-sm mt-1">Spin every 12 hours!</p>
      </div>

      <div className="relative w-72 h-72 mx-auto my-8">
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-500 via-yellow-300 to-yellow-500 animate-pulse opacity-30 blur-xl"></div>
        
        <div className="absolute inset-0 rounded-full border-4 border-yellow-500 shadow-[0_0_30px_rgba(255,215,0,0.5)]">
          {[...Array(24)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full"
              style={{
                top: '50%',
                left: '50%',
                transform: `rotate(${i * 15}deg) translateY(-140px) translateX(-50%)`,
              }}
            />
          ))}
        </div>

        <div
          className="absolute inset-3 rounded-full overflow-hidden shadow-2xl transition-transform ease-out"
          style={{ 
            transform: `rotate(${rotation}deg)`,
            transitionDuration: spinning ? '5s' : '0s',
            transitionTimingFunction: 'cubic-bezier(0.17, 0.67, 0.12, 0.99)'
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <defs>
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#FFD700" floodOpacity="0.5"/>
              </filter>
              <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700"/>
                <stop offset="50%" stopColor="#FFA500"/>
                <stop offset="100%" stopColor="#FFD700"/>
              </linearGradient>
              <linearGradient id="darkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2a2a2a"/>
                <stop offset="50%" stopColor="#1a1a1a"/>
                <stop offset="100%" stopColor="#2a2a2a"/>
              </linearGradient>
            </defs>
            {segments.map((seg, i) => {
              const angle = 360 / segments.length;
              const startAngle = i * angle - 90;
              const endAngle = startAngle + angle;
              const x1 = 50 + 50 * Math.cos((startAngle * Math.PI) / 180);
              const y1 = 50 + 50 * Math.sin((startAngle * Math.PI) / 180);
              const x2 = 50 + 50 * Math.cos((endAngle * Math.PI) / 180);
              const y2 = 50 + 50 * Math.sin((endAngle * Math.PI) / 180);
              const textAngle = startAngle + angle / 2;
              const textX = 50 + 32 * Math.cos((textAngle * Math.PI) / 180);
              const textY = 50 + 32 * Math.sin((textAngle * Math.PI) / 180);
              
              return (
                <g key={i}>
                  <path 
                    d={`M50,50 L${x1},${y1} A50,50 0 0,1 ${x2},${y2} Z`} 
                    fill={seg.color === "#FFD700" ? "url(#goldGrad)" : "url(#darkGrad)"}
                    stroke="#333"
                    strokeWidth="0.5"
                  />
                  <text 
                    x={textX} 
                    y={textY} 
                    textAnchor="middle" 
                    dominantBaseline="middle" 
                    fill={seg.color === "#FFD700" ? "#000" : "#FFD700"} 
                    fontSize="7" 
                    fontWeight="bold"
                    filter="url(#shadow)"
                    transform={`rotate(${textAngle + 90}, ${textX}, ${textY})`}
                  >
                    {seg.label}
                  </text>
                </g>
              );
            })}
            <circle cx="50" cy="50" r="12" fill="url(#goldGrad)" stroke="#B8860B" strokeWidth="2"/>
            <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fill="#000" fontSize="6" fontWeight="bold">LTG</text>
          </svg>
        </div>

        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
          <div className="relative">
            <div className="w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-b-[25px] border-b-yellow-500 drop-shadow-lg"></div>
            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[18px] border-b-yellow-300"></div>
          </div>
        </div>
      </div>

      <div className="text-center mt-8">
        <button
          onClick={doSpin}
          disabled={spinning || !spinStatus?.can_spin}
          className={`relative px-16 py-5 rounded-full font-bold text-xl overflow-hidden transition-all duration-300 ${
            spinStatus?.can_spin && !spinning 
              ? "bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-500 text-black shadow-[0_0_30px_rgba(255,215,0,0.5)] hover:shadow-[0_0_50px_rgba(255,215,0,0.7)] hover:scale-105" 
              : "bg-gray-800 text-gray-500 cursor-not-allowed"
          }`}
          data-testid="spin-btn"
        >
          {spinning && (
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></span>
          )}
          {spinning ? "✨ SPINNING..." : spinStatus?.can_spin ? "🎰 SPIN NOW" : "⏳ WAIT"}
        </button>
        
        {!spinStatus?.can_spin && countdown && (
          <div className="mt-4 p-3 bg-gradient-to-r from-yellow-500/10 via-yellow-500/20 to-yellow-500/10 rounded-xl border border-yellow-500/30">
            <p className="text-yellow-400 text-sm">Next spin in:</p>
            <p className="text-yellow-500 font-mono text-2xl font-bold">{countdown}</p>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-center gap-6">
        <div className="text-center p-4 bg-gradient-to-b from-yellow-500/10 to-transparent rounded-xl border border-yellow-500/20">
          <p className="text-muted-foreground text-xs">Total Won</p>
          <p className="text-yellow-500 font-manrope text-2xl font-bold">{spinStatus?.total_won || 0}</p>
          <p className="text-yellow-500/70 text-xs">LTG</p>
        </div>
        <div className="text-center p-4 bg-gradient-to-b from-green-500/10 to-transparent rounded-xl border border-green-500/20">
          <p className="text-muted-foreground text-xs">Spins</p>
          <p className="text-green-500 font-manrope text-2xl font-bold">2x</p>
          <p className="text-green-500/70 text-xs">Daily</p>
        </div>
      </div>

      {showReward && reward !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowReward(false)}>
          <div className="bg-gradient-to-b from-gray-900 to-black p-8 rounded-3xl border-2 border-yellow-500 shadow-[0_0_50px_rgba(255,215,0,0.3)] text-center animate-bounce-in">
            {reward > 0 ? (
              <>
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-3xl font-bold text-gold-gradient mb-2">WINNER!</h2>
                <p className="text-5xl font-bold text-yellow-500 mb-2">{reward} LTG</p>
                <p className="text-muted-foreground">Added to your wallet!</p>
              </>
            ) : (
              <>
                <div className="text-6xl mb-4">😔</div>
                <h2 className="text-2xl font-bold text-muted-foreground mb-2">Better Luck Next Time!</h2>
                <p className="text-muted-foreground">Try again in 12 hours</p>
              </>
            )}
            <button className="mt-6 px-8 py-2 bg-yellow-500 text-black rounded-full font-bold">OK</button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SpinPage;
